import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import {
  Rocket,
  Bot,
  Gift,
  PhoneCall,
  Headphones,
  Sparkles,
  Film,
  FileEdit,
  LayoutDashboard,
  Crosshair,
  Search,
  Megaphone,
  Users2,
  ArrowRight,
} from "lucide-react";

const BENEFITS = [
  {
    icon: Rocket,
    title: "Best of Best Listing Funnel",
    body: "A premium business profile built to work as a 24/7 lead engine — not just a directory page.",
  },
  {
    icon: Bot,
    title: "Trained AI Business Hero",
    body: "An AI chat assistant trained around your business, services, FAQs, offers, and customer questions.",
  },
  {
    icon: Gift,
    title: "LocalBest Rewards & Offers",
    body: "Visitors can claim coupons, free resort stays, and travel rewards sponsored through LocalBest.ai to motivate action.",
  },
  {
    icon: PhoneCall,
    title: "Call Now + Lead Capture",
    body: "Tracked Call-Now buttons, request-help forms, and offer claims feed every visitor straight into your dashboard.",
  },
  {
    icon: Headphones,
    title: "Optional AI Voice Assistant",
    body: "Upgraded listings get an AI voice that handles calls, FAQs, bookings, live transfers, and sends recordings + transcripts after every call.",
  },
  {
    icon: Sparkles,
    title: "Instant Brand Match",
    body: "Add your website and we automatically pull in your logo, brand colors, images, and business details so your listing looks branded from day one.",
  },
  {
    icon: Film,
    title: "Cinematic Commercials & Video",
    body: "Showcase a cinematic commercial, project videos, and brand storytelling content right inside your listing funnel.",
  },
  {
    icon: FileEdit,
    title: "Live Content Publishing",
    body: "Publish photos, videos, services, coupons, FAQs, hours, social links, project highlights, and local updates anytime.",
  },
  {
    icon: LayoutDashboard,
    title: "Business Dashboard",
    body: "One place for leads, analytics, content, profile updates, API automations, and listing performance.",
  },
  {
    icon: Crosshair,
    title: "Retargeting-Ready Tracking",
    body: "Every visitor is tracked and tagged so your campaigns can follow interested prospects across social, search, video, display, TV, audio, and direct mail — where available.",
  },
  {
    icon: Search,
    title: "Built for Visibility",
    body: "Designed to improve your visibility across LocalBest.ai, Google Search, AI Search, social media, and local discovery.",
  },
  {
    icon: Megaphone,
    title: "Promotion & Ads Support",
    body: "Available on upgraded plans: paid ads, social, video, retargeting, and campaign support that pushes your funnel everywhere — all at once.",
  },
  {
    icon: Users2,
    title: "AI Horse-Powered Workspace",
    body: "Qualified businesses unlock unlimited AI agents, digital assistants, A2P-approved two-way text + email automations, ringless voicemail drops, an Executive Assistant, a Paid Ads Team, and 40 departments of real human experts for sales, marketing, and operations.",
  },
];

export default function ListingBenefitsSection({
  title = "What Every Active Best of Best Listing Includes",
  subtitle = "Your listing isn't a profile — it's an AI-powered local lead engine. Here's everything that can come with an active Best of Best Listing.",
  showCta = true,
  compact = false,
}) {
  return (
    <section className="relative py-16 md:py-24" data-testid="listing-benefits-section">
      <div className="container mx-auto px-4 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mb-12"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-[#00E5FF] font-medium mb-3">
            More than a listing
          </p>
          <h2 className="text-3xl md:text-5xl font-bold font-['Outfit'] text-white leading-tight mb-4">
            {title}
          </h2>
          <p className="text-base md:text-lg text-[#94A3B8] leading-relaxed">
            {subtitle}
          </p>
        </motion.div>

        <div className={`grid gap-4 ${compact ? "md:grid-cols-2 lg:grid-cols-3" : "md:grid-cols-2 lg:grid-cols-3"}`}>
          {BENEFITS.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.03 }}
              className="glass-card p-5 rounded-2xl border border-white/5 hover:border-[#7C3AED]/40 transition-all group"
              data-testid={`benefit-card-${i}`}
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7C3AED]/20 to-[#00E5FF]/10 border border-white/10 flex items-center justify-center mb-3 group-hover:from-[#7C3AED]/40 group-hover:to-[#00E5FF]/20 transition-all">
                <b.icon className="w-5 h-5 text-[#00E5FF]" />
              </div>
              <h3 className="text-white font-semibold mb-1.5 font-['Outfit']">{b.title}</h3>
              <p className="text-sm text-[#94A3B8] leading-relaxed">{b.body}</p>
            </motion.div>
          ))}
        </div>

        {showCta && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 flex flex-wrap items-center justify-center gap-3"
          >
            <Link to="/submit-business">
              <Button className="bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] hover:from-[#00E5FF] hover:to-[#7C3AED] text-white font-semibold h-12 px-6" data-testid="benefits-create-listing-btn">
                <Sparkles className="w-4 h-4 mr-2" /> Create My Free Listing
              </Button>
            </Link>
            <Link to="/how-it-works">
              <Button variant="outline" className="border-white/15 text-white hover:bg-white/5 h-12 px-6" data-testid="benefits-how-it-works-btn">
                See How It Works <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/get-funded">
              <Button variant="outline" className="border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/10 h-12 px-6" data-testid="benefits-get-funded-btn">
                Co-Marketing & Funding Support
              </Button>
            </Link>
          </motion.div>
        )}

        <p className="text-center text-xs text-[#64748B] mt-8 max-w-3xl mx-auto leading-relaxed">
          Some features are available on upgraded plans or for qualified businesses. Promotion, ads support, AI voice, and workspace features may require eligibility, approval, or activation. Where available.
        </p>
      </div>
    </section>
  );
}
